import type { VoidFunctionComponent } from 'react';
import * as _ from 'lodash';

import PackageLabelBadge from './PackageLabelBadge';
import DynamicShowMoreList from '../../generic/DynamicShowMoreList';
import styles from './PackageLabelsList.module.less';
import { sortPackageLabelsByScopeAndName } from './utils';
import type Locator from '../../../../shared/Locator';
import type { PackageLabelAssignmentWithName } from '../../../../shared/model-typings/packagelabelassignment';

type Props = {
  labels?: PackageLabelAssignmentWithName[];
  dependencyVersion?: string;
  dependencyLocator?: Locator;
  hasDarkBackground?: boolean;
  enforceUnique?: boolean;
  /**
   * If set, the label prefix will be added to the beginning of each label name.
   * Defaults to "Labels " if not set.
   */
  labelPrefix?: string;
};

/**
 * Horizontal list of package label badges with dynamic "show more" behavior.
 */
const PackageLabelsList: VoidFunctionComponent<Props> = ({
  labels,
  dependencyVersion,
  dependencyLocator,
  hasDarkBackground = false,
  enforceUnique = false,
  labelPrefix = 'Labels ',
}) => {
  // If the dependency version is not set, use the locator revision value
  const versionToCheck = dependencyVersion || dependencyLocator?.revision.value;
  const filteredLabels = labels?.filter(
    (label) => label.packageVersion === versionToCheck || label.packageVersion == null,
  );

  if (!filteredLabels?.length) {
    return null;
  }

  const packageLabels = enforceUnique ? _.uniqBy(filteredLabels, label => label.name) : filteredLabels;

  const badges = sortPackageLabelsByScopeAndName(packageLabels).map((label) => {
    return <PackageLabelBadge label={label} hasDarkBackground={hasDarkBackground} />;
  });

  return (
    <div className={styles.packageLabelsList}>
      <DynamicShowMoreList list={[labelPrefix, ...badges]} lineHeight={27} />
    </div>
  );
};

export default PackageLabelsList;
